import React, { useState } from 'react';
import { ChevronDown, Clock } from 'lucide-react';

export interface QuoteRequestFormData {
  firstName: string;
  lastName: string;
  weddingDate: string;
  isFlexible: boolean;
  guestCount: number;
  phone: string;
  vision: string;
}

interface QuoteRequestFormProps {
  formData: QuoteRequestFormData;
  onFormDataChange: (data: QuoteRequestFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  variant?: 'modal' | 'inline';
  className?: string;
  showResponseTime?: boolean;
}

export const QuoteRequestForm: React.FC<QuoteRequestFormProps> = ({
  formData,
  onFormDataChange,
  onSubmit,
  isSubmitting,
  variant = 'inline',
  className = '',
  showResponseTime = true
}) => {
  const [showMore, setShowMore] = useState(false);

  // Theme configuration based on variant
  const theme = {
    modal: {
      input: 'border-white/10 bg-[#3B2D56]/70 text-white placeholder-white/60',
      button: 'bg-gradient-to-r from-[#FF4E8E] to-[#FF6B6B] text-white',
      text: 'text-white/80',
      link: 'text-white'
    },
    inline: {
      input: 'border-pink-100/50 bg-white/80 text-gray-800 placeholder-gray-500',
      button: 'bg-gradient-to-r from-[#FF4E8E] to-[#FF6B6B] text-white',
      text: 'text-gray-700',
      link: 'text-[#FF4E8E]'
    }
  }[variant];

  const handleChange = (field: keyof QuoteRequestFormData, value: any) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  return (
    <form onSubmit={onSubmit} className={`space-y-1 md:space-y-2 ${className}`}>
      {showResponseTime && (
        <div className="flex items-center justify-center mt-2">
          <div className={`flex items-center gap-1 text-[#FF4E8E] text-xs sm:text-sm`}>
            <Clock size={14} />
            <span>Typically responds within 24h</span>
          </div>
        </div>
      )}

      {/* Names in one row */}
      <div className="grid grid-cols-2 gap-1 md:gap-2">
        <input
          type="text"
          placeholder="First name*"
          required
          className={`w-full px-2 md:px-3 py-1 md:py-1.5 rounded-lg border ${theme.input} focus:border-[#FF4E8E] focus:outline-none text-[11px] md:text-sm`}
          value={formData.firstName}
          onChange={(e) => handleChange('firstName', e.target.value)}
        />
        <input
          type="text"
          placeholder="Last name*"
          required
          className={`w-full px-2 md:px-3 py-1 md:py-1.5 rounded-lg border ${theme.input} focus:border-[#FF4E8E] focus:outline-none text-[11px] md:text-sm`}
          value={formData.lastName}
          onChange={(e) => handleChange('lastName', e.target.value)}
        />
      </div>

      {/* Wedding Date Row - Combined with checkbox */}
      <div className="flex gap-1 md:gap-2 items-center">
        <div className="flex-1 relative">
          <input
            type="date"
            placeholder="Wedding date*"
            required
            className={`w-full px-2 md:px-3 py-1 md:py-1.5 rounded-lg border ${theme.input} focus:border-[#FF4E8E] focus:outline-none text-[11px] md:text-sm appearance-none`}
            value={formData.weddingDate}
            onChange={(e) => handleChange('weddingDate', e.target.value)}
          />
          <div className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={variant === 'modal' ? 'white' : '#FF4E8E'} strokeWidth="1.5" opacity="0.6" className="md:w-5 md:h-5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
        </div>
        <div className="flex items-center whitespace-nowrap">
          <input
            type="checkbox"
            id="flexible"
            className={`rounded h-2.5 w-2.5 md:h-3 md:w-3 border-${variant === 'modal' ? 'white/30' : 'pink-200'} bg-${variant === 'modal' ? '[#3B2D56]/70' : 'white/80'} text-[#FF4E8E] focus:ring-0`}
            checked={formData.isFlexible}
            onChange={(e) => handleChange('isFlexible', e.target.checked)}
          />
          <label htmlFor="flexible" className={`${theme.text} text-[10px] md:text-xs ml-1`}>Date flexible</label>
        </div>
      </div>

      {/* Message/Vision field */}
      <div>
        <textarea
          placeholder="Your message*"
          required
          rows={2}
          className={`w-full px-2 md:px-3 py-1 md:py-1.5 rounded-lg border ${theme.input} focus:border-[#FF4E8E] focus:outline-none resize-none text-[11px] md:text-sm`}
          value={formData.vision}
          onChange={(e) => handleChange('vision', e.target.value)}
        />
      </div>

      {/* Show More/Less Toggle */}
      <button 
        type="button"
        onClick={() => setShowMore(!showMore)}
        className={`w-full flex items-center justify-center gap-1 ${theme.text} text-[10px] md:text-xs py-0.5 md:py-1`}
      >
        {showMore ? 'Show less' : 'Show more options'} 
        <ChevronDown size={12} className={`transition-transform md:w-4 md:h-4 ${showMore ? 'rotate-180' : ''}`} />
      </button>

      {/* Optional Fields - Only show when expanded */}
      {showMore && (
        <>
          {/* Guest Count */}
          <div className="pt-0 pb-0">
            <div className={`flex justify-between items-center text-[10px] md:text-xs ${theme.text}`}>
              <span>Guest Count: {formData.guestCount}</span>
              <div className="flex gap-1 text-[10px] md:text-xs text-gray-500">
                <span>0</span>
                <span>150</span>
                <span>300+</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="300"
              className="w-full h-0.5 md:h-1 my-0.5 md:my-1 bg-pink-100/50 rounded-full appearance-none cursor-pointer accent-[#FF4E8E]"
              value={formData.guestCount}
              onChange={(e) => handleChange('guestCount', parseInt(e.target.value))}
            />
          </div>

          {/* Phone */}
          <div>
            <input
              type="tel"
              placeholder="Phone number (optional)"
              className={`w-full px-2 md:px-3 py-1 md:py-1.5 rounded-lg border ${theme.input} focus:border-[#FF4E8E] focus:outline-none text-[11px] md:text-sm`}
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Terms & Submit Row */}
      <div className="pt-1 md:pt-2">
        <p className={`text-[9px] md:text-xs ${theme.text} leading-tight text-center`}>
          By clicking 'Send', you agree to Wedify creating an account for you and to our Terms of Use.
        </p>
        
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-1.5 md:py-2 mt-1 md:mt-2 rounded-full ${theme.button} text-xs md:text-sm font-medium shadow-lg hover:shadow-xl transition-all ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
        
        <div className="flex justify-center mt-1 md:mt-2">
          <a href="#" className={`text-center ${theme.link} text-[10px] md:text-xs underline decoration-1 underline-offset-2`}>
            Why use Wedify?
          </a>
        </div>
      </div>
    </form>
  );
};